import React from 'react';
import { Input, Avatar, Tooltip, Button, Row, Col, Divider } from 'antd';
import { SendOutlined } from '@ant-design/icons';
import moment from 'moment';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';

import style from './ChatContainer.scss';

const AlwaysScrollToBottom = () => {
  const ref = React.useRef(null);
  React.useEffect(() => ref.current?.scrollIntoView({ behavior: 'smooth' }));
  return <div ref={ref} />;
};

class Chat extends React.Component {
  state = { message: '' };

  handleMessage = e => this.setState({ message: e.target.value });
  sendMessage = () => {
    const msg = this.state.message.trim();
    if (!msg) return;
    this.props.sendMessage?.(msg);
    this.setState({ message: '' });
  };

  // Helpers
  initialsOf = (name = '') =>
    name
      .split(' ')
      .filter(Boolean)
      .slice(0, 2)
      .map(s => s[0].toUpperCase())
      .join('') || 'U';

  time = ts => moment(ts).format('h:mm A');

  renderBubble = ({ text, ts, isSelf, name, colorSelf = '#FFC043', colorOther = '#36CFC9' }) => {
    const initials = this.initialsOf(name);
    return (
      <div
        className={`${style.msgRow} ${isSelf ? style.self : style.other}`}
        key={`${ts}-${text}`}
      >
        {/* Edge avatar */}
        {!isSelf ? (
          <Avatar
            className={style.edgeAvatar}
            style={{ backgroundColor: colorOther }}
          >
            {initials}
          </Avatar>
        ) : (
          <Avatar
            className={`${style.edgeAvatar} ${style.edgeAvatarRight}`}
            style={{ backgroundColor: colorSelf, color: '#000' }}
          >
            {initials || 'You'}
          </Avatar>
        )}

        {/* Bubble + time */}
        <div className={style.bubbleCol}>
          <div className={`${style.bubble} ${isSelf ? style.bubbleSelf : style.bubbleOther}`}>
            {text}
          </div>
          <div className={`${style.time} ${isSelf ? style.timeSelf : style.timeOther}`}>
            <Tooltip title={moment(ts).format('DD-MM-YY, h:mm:ss a')}>{this.time(ts)}</Tooltip>
          </div>
        </div>
      </div>
    );
  };

  // Old persisted chat items
  renderOldChat = () => {
    const { chat_data = [], participants = [], user_id } = this.props;
    return chat_data.map(item => {
      const p = participants.find(x => x.user_id === item.created_by);
      const name =
        item.created_by === user_id ? this.props.currentUserName || 'You' : p?.name || '';
      const ts = item.fire_time;
      if (item.type === 'entry' || item.type === 'exit') {
        return (
          <div
            className={style.chatStatus}
            key={`${item.type}-${ts}`}
          >
            <p>
              {item.created_by === user_id ? 'You' : name}{' '}
              {item.type === 'entry' ? 'entered' : 'left'} the chat
            </p>
          </div>
        );
      }
      return this.renderBubble({
        text: item.data,
        ts,
        isSelf: item.created_by === user_id,
        name,
      });
    });
  };

  // Live session chat items
  renderChat = () => {
    const { chat = [], user_id } = this.props;
    return chat.map(item => {
      if (item.type === 'entry' || item.type === 'exit') {
        return (
          <div
            className={style.chatStatus}
            key={`${item.type}-${item.fire_time}`}
          >
            <p>
              {item.user_id === user_id ? 'You' : item.user}{' '}
              {item.type === 'entry' ? 'entered' : 'left'} the chat
            </p>
          </div>
        );
      }
      return this.renderBubble({
        text: item.message,
        ts: item.fire_time,
        isSelf: item.user_id === user_id,
        name: item.user,
      });
    });
  };

  render() {
    return (
      <div className={style.chatCard}>
        {/* Header */}
        <div className={style.header}>
          <span>Chat</span>
          <Divider className={style.headerDivider} />
        </div>

        {/* Body */}
        <div className={style.body}>
          {this.props.chat_data?.length ? this.renderOldChat() : null}
          {this.props.chat?.length ? this.renderChat() : null}
          <AlwaysScrollToBottom />
        </div>

        {/* Footer */}
        <div className={style.footer}>
          {/* (Optional) typing chips container – render if you have typing users */}
          {Boolean(this.props.typingUsers?.length) && (
            <div className={style.typingPills}>
              {this.props.typingUsers.map(u => (
                <Avatar
                  key={u.id || u.name}
                  size={24}
                  style={{ backgroundColor: u.color || '#1677ff' }}
                >
                  {this.initialsOf(u.name)}
                </Avatar>
              ))}
            </div>
          )}

          <Row
            gutter={8}
            align="middle"
            wrap={false}
          >
            <Col flex="auto">
              <Input
                placeholder="Type a message..."
                value={this.state.message}
                onChange={this.handleMessage}
                onPressEnter={this.sendMessage}
                size="large"
                className={style.input}
              />
            </Col>
            <Col>
              <Button
                type="primary"
                size="large"
                shape="round"
                icon={<SendOutlined />}
                onClick={this.sendMessage}
                disabled={!this.state.message.trim()}
                className={style.sendBtn}
                aria-label="Send"
              />
            </Col>
          </Row>
        </div>
      </div>
    );
  }
}

const mapStateToProps = state => ({
  user_id: state.auth.user_id,
  participants: state.interview.participants,
  currentUserName: state.auth?.name, // if available; falls back to "You"
});
export default withRouter(connect(mapStateToProps)(Chat));
